import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor, HttpRequest, HttpErrorResponse
} from '@angular/common/http';
import {
  firstValueFrom,
  defer,
  from,
  iif,
  Observable, of,
  throwError,
  timer
} from 'rxjs';

import { Inject, Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';

import {
  catchError,
  first,
  map,
  switchMap,
  take,
  tap,
  timeout
} from 'rxjs/operators';

import { AuthService } from '@auth0/auth0-angular';
import { FEATURE_FLAG_SERVICE, FeatureFlagKey, IFeatureFlagService } from '@sondermind/data-access/feature-flags';
import { Logger } from '@sondermind/datadog-logging';
import { ConfigurationService } from '@sondermind/configuration';
import { UrlTargetService } from '@sondermind/data-access/auth-utils';
import { SegmentSchema, SegmentService } from '@sondermind/segment';
import { Auth0TokenService } from '../services/auth0.service';
import { AuthTokenService } from '../services/auth_token.service';
import { UNAUTHORIZED_REDIRECT_ROUTE } from '../tokens/unauthorized-redirect-route.token';

interface I403ErrorSegmentSchema extends SegmentSchema {
  'Provider 403 Error': { url: string; method: string; retry: boolean; };
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  useAuth0Fix = false;
  constructor(
    private authTokenService: AuthTokenService,
    private configurationService: ConfigurationService,
    private router: Router,
    private auth0Service: Auth0TokenService,
    @Optional()
    @Inject(UNAUTHORIZED_REDIRECT_ROUTE)
    private unauthorizedRedirectRoute: string | null,
    private auth0: AuthService,
    private urlTargetService: UrlTargetService,
    private logger: Logger,
    @Inject(FEATURE_FLAG_SERVICE)
    private featureFlagService: IFeatureFlagService,
    private segmentService: SegmentService<I403ErrorSegmentSchema>,
  ) {
    this.initializeAuth0Fix();
  }

  private async initializeAuth0Fix(): Promise<void> {
    // Check feature flag - await to ensure it's set before continuing
    this.useAuth0Fix = await firstValueFrom(
      this.featureFlagService.isEnabled$(FeatureFlagKey.USE_AUTH0_FIX).pipe(
        timeout(500), // Don't block auth if feature flag service is slow
        take(1)
      )
    ).catch(() => false); // Default to false if timeout/error
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.interceptCore(req, next, true);
  }

  private interceptCore(req: HttpRequest<any>, next: HttpHandler, retryOnFailure: boolean): Observable<HttpEvent<any>> {
    // bail if we're not requesting something against our api
    if (!this.urlTargetService.targetsSonderMindApis(req)) { return next.handle(req) }

    // bail if skipAuthorization was used (Authorization: 'None' header)
    // or if the request already has a valid authorization header during OAuth flow
    const authHeader = req.headers.get('Authorization');
    if (authHeader === 'None') {
      return next.handle(req).pipe(
        catchError((e) => this.errorHandler(req, next, e, retryOnFailure))
      );
    }
    if (req.headers.has('Authorization') && !this.auth0Service.auth0Enabled) {
      return next.handle(req).pipe(
        catchError((e) => this.errorHandler(req, next, e, retryOnFailure))
      );
    }

    const origReq = req.clone();
    return this.auth0Service.auth0Enabled$.pipe(
      take(1),
      switchMap((enabled) => iif(
        () => enabled,
        defer(() => this.auth0.isAuthenticated$.pipe(
          take(1),
          switchMap((isAuthenticated) => {
            if (!isAuthenticated) {
              // Not authenticated - don't try to get token, proceed without auth
              this.logger.debug('Not authenticated, proceeding without auth');
              return of('');
            }
            // Use barrier method to get token - prevents concurrent refresh token exchanges
            return this.auth0Service.getTokenWithBarrier().pipe(
              catchError((err) => {
                const errorMessage = err instanceof Error ? err.message : String(err);
                // During initialization or when refresh token is missing/invalid,
                // Auth0 may not be able to refresh. Proceed without auth instead of failing immediately.
                // The request will likely fail with 401, which triggers a retry with forceRefresh.
                this.logger.debug('Failed to get Auth0 token, proceeding without auth', { error: errorMessage });
                return of('');
              })
            );
          })
        )),
        this.resolveOAuthToken()
      )),
      switchMap((token) => {
        const reqWithAuth = token
          ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
          : req;
        return next.handle(reqWithAuth);
      }),
      catchError((e) => this.errorHandler(origReq, next, e, retryOnFailure))
    );
  }
  private resolveAuth0Token$(): Observable<string> {
    return this.auth0Service.auth0Token$;
  }

  // if there's no provided token resolver, we do it naively with the oauth service
  private resolveOAuthToken(): Observable<string> {
    // if we have a valid token, use it
    if (this.authTokenService.isAuthenticated()) {
      return of(this.authTokenService.getAuthToken());
    }

    // otherwise do a silent refresh loop
    return from(this.authTokenService.silentRefresh()).pipe(
      map(() => this.authTokenService.getAuthToken())
    );
  }

  // reruns the given request after a delay if the failure was authentication related
  private errorHandler(
    req: HttpRequest<any>,
    next: HttpHandler,
    error: any,
    retry: boolean
  ): Observable<HttpEvent<any>> {
    const httpError = error as HttpErrorResponse;
    // Using feature flag to stop treating 403s as token refresh candidates.
    const isAuthFailure = this.useAuth0Fix ?
      httpError?.status === 401 :
      httpError?.status === 401 || httpError?.status === 403;

    // Sending 403 errors to amplitude so we can find journeys leading to this error
    if (httpError?.status === 403) {
      this.segmentService.track('Provider 403 Error', { url: req.url, method: req.method, retry });
    }

    if (isAuthFailure) {
      if (retry) {
        return timer(500).pipe(
          switchMap(() => this.interceptCore(req, next, false))
        );
      }

      if (this.auth0Service.auth0Enabled) {
        this.logger.info('Handling Auth0 authentication failure', { url: req.url });
        return this.auth0.isAuthenticated$.pipe(
          first(),
          switchMap((isAuthenticated) => {
            if (isAuthenticated) {
              this.logger.info('User is authenticated with Auth0, attempting to refresh token', { url: req.url });
              // Use barrier method with forceRefresh to ensure we get a fresh token after 401
              // The barrier prevents race conditions when multiple requests fail simultaneously
              return this.auth0Service.getTokenWithBarrier(true).pipe(
                tap(() => {
                  this.logger.info('Successfully refreshed Auth0 token via barrier');
                }),
                catchError((tokenError) => {
                  this.logger.error('Auth0 token refresh failed, logging out', { error: tokenError.message });
                  this.auth0Service.logout();
                  return throwError(() => new Error('Token refresh failed'));
                }),
                switchMap((token) => {
                  this.logger.debug('Updated Auth0 token and retrying request', { url: req.url });
                  const newReq = req.clone({
                    setHeaders: {
                      Authorization: `Bearer ${token}`
                    }
                  });

                  return next.handle(newReq);
                })
              );
            }
            return this.redirectAndLogout(req, error);
          })
        );
      }
      return this.redirectAndLogout(req, error);
    }

    return throwError(() => error);
  }

  private redirectAndLogout(req: HttpRequest<unknown>, error: unknown): Observable<null> {
    this.logger.debug('Logging out due to 401 or 403', { request: req, error });

    const redirectRoute = this.unauthorizedRedirectRoute
      || `${this.configurationService.env.authBase}/session/logout`;

    return of(this.router.navigateByUrl(redirectRoute)).pipe(
      tap(() => this.authTokenService.logout()),
      switchMap(() => of(null))
    );
  }
}
